import * as React from 'react';
import './ImageSelect.css';

interface IImageSelectOption {
  className?: string;
  selectedValue: string;
  value: string;
  /** ツールチップに出す選択肢の名前 */
  label: string;
  onClick: (value: string) => void;
}

const ImageSelectOption: React.FC<IImageSelectOption> = function (props) {
  const selected = props.selectedValue === props.value;
  let className = 'ImageSelectOption-container';
  if (selected) {
    className += ' ImageSelectOption-selected';
  }

  let buttonClassName = 'ImageSelectOption-button';
  if (props.className) {
    buttonClassName += ` ${props.className}`;
  }

  return (
    <button
      type="button"
      className={buttonClassName}
      title={props.label}
      aria-pressed={selected}
      onClick={() => props.onClick(props.value)}
    >
      <div className={className}>{props.children}</div>
    </button>
  );
};

const LINE_TYPES: { src: string; label: string }[] = [
  { src: '/line_normal.png', label: '実線' },
  { src: '/line_dotted.png', label: '点線' },
  { src: '/line_dashed.png', label: '破線' },
  { src: '/line_circle.png', label: '丸と線の破線' },
];

const ARROW_END_TYPES: { src: string; label: string }[] = [
  { src: '/arrow_end_none.png', label: '線の端: なし' },
  { src: '/arrow_end_arrow.png', label: '線の端: 矢印' },
  { src: '/arrow_end_circle.png', label: '線の端: 丸' },
  { src: '/arrow_end_cross.png', label: '線の端: バツ印' },
];

const HATCH_TYPES: { src: string; label: string }[] = [
  { src: '/hatch_none.png', label: 'ハッチなし' },
  { src: '/hatch_line.png', label: '斜線（細かい）' },
  { src: '/hatch_lineL.png', label: '斜線（粗い）' },
  { src: '/hatch_cross.png', label: '斜め格子（粗い）' },
  { src: '/hatch_crossL.png', label: '斜め格子（細かい）' },
  { src: '/hatch_fill.png', label: '塗りつぶし' },
  { src: '/hatch_diagonal_cross.png', label: '対角線のバツ印' },
];

export const LineImage: React.FC<{ id: number }> = function (props) {
  const lineType = LINE_TYPES[props.id];
  if (!lineType) {
    throw new Error('unknown line type: ' + props.id);
  }
  return <img alt={lineType.label} title={lineType.label} src={lineType.src} />;
};

interface ILineSelectProps {
  value: number;
  optionClassName?: string;
  containerClassName?: string;
  onChange: (value: number) => void;
}

export const LineSelect: React.FC<ILineSelectProps> = function (props) {
  return (
    <div className={props.containerClassName}>
      {LINE_TYPES.map((lineType, i) =>
        <ImageSelectOption
          key={i}
          className={props.optionClassName}
          selectedValue={props.value.toString()}
          value={i.toString()}
          label={lineType.label}
          onClick={(v) => props.onChange(parseInt(v))}
        >
          <LineImage id={i} />
        </ImageSelectOption>
      )}
    </div>
  );
};

interface IArrowEndSelectProps {
  value: number;
  optionClassName?: string;
  containerClassName?: string;
  onChange: (value: number) => void;
}

export const ArrowEndSelect: React.FC<IArrowEndSelectProps> = function (props) {
  return (
    <div className={props.containerClassName}>
      {ARROW_END_TYPES.map((arrowEndType, i) =>
        <ImageSelectOption
          key={i}
          className={props.optionClassName}
          selectedValue={props.value.toString()}
          value={i.toString()}
          label={arrowEndType.label}
          onClick={(v) => props.onChange(parseInt(v))}
        >
          <img alt={arrowEndType.label} src={arrowEndType.src} />
        </ImageSelectOption>
      )}
    </div>
  );
};

interface IEnclosingSvg {
  id: number;
  svg: string;
}

interface IEnclosingSvgSelectProps {
  enclosingSvgs: IEnclosingSvg[];
  value: number | null | undefined;
  optionClassName?: string;
  containerClassName?: string;
  onChange: (value: number | null) => void;
}

export const EnclosingSvgSelect: React.FC<IEnclosingSvgSelectProps> = function (props) {
  const [showDialog, setShowDialog] = React.useState(false);
  const selectedSvg = props.enclosingSvgs.find(svg => svg.id === props.value);

  // lazy import to avoid circular dependency
  const SvgSelectDialogRef = React.useRef<typeof import('./SvgSelectDialog').SvgSelectDialog | null>(null);
  const [dialogLoaded, setDialogLoaded] = React.useState(false);

  React.useEffect(() => {
    import('./SvgSelectDialog').then(module => {
      SvgSelectDialogRef.current = module.SvgSelectDialog;
      setDialogLoaded(true);
    });
  }, []);

  const handleSelect = (id: number | null) => {
    props.onChange(id);
    setShowDialog(false);
  };

  const SvgSelectDialog = SvgSelectDialogRef.current;

  return (
    <div className={props.containerClassName}>
      <button
        type="button"
        className={props.optionClassName}
        title="囲み図形を選択"
        onClick={() => setShowDialog(true)}
      >
        {(function () {
          if (!selectedSvg) {
            return <span className="EnclosingSvgSelect-none">なし</span>;
          }
          return (
            <img
              className="EnclosingSvgSelect-svg"
              alt="選択中の囲み図形"
              src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(selectedSvg.svg).replace(/'/g, "%27").replace(/"/g, "%22")}`}
            />
          );
        })()}
      </button>
      {dialogLoaded && SvgSelectDialog && (
        <SvgSelectDialog
          open={showDialog}
          enclosingSvgs={props.enclosingSvgs}
          selectedValue={props.value}
          onSelect={handleSelect}
          onCancel={() => setShowDialog(false)}
        />
      )}
    </div>
  );
};

interface IHatchSelectProps {
  value: number;
  optionClassName?: string;
  containerClassName?: string;
  onChange: (value: number) => void;
}

export const HatchImage: React.FC<{ id: number }> = function (props) {
  const hatchType = HATCH_TYPES[props.id];
  if (!hatchType) {
    throw new Error('unknown hatch type: ' + props.id);
  }
  return <img alt={hatchType.label} title={hatchType.label} src={hatchType.src} />;
};

export const HatchSelect: React.FC<IHatchSelectProps> = function (props) {
  return (
    <div className={props.containerClassName}>
      {HATCH_TYPES.map((hatchType, i) =>
        <ImageSelectOption
          key={i}
          className={props.optionClassName}
          selectedValue={props.value.toString()}
          value={i.toString()}
          label={hatchType.label}
          onClick={(v) => props.onChange(parseInt(v))}
        >
          <HatchImage id={i} />
        </ImageSelectOption>
      )}
    </div>
  );
};
